import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'

interface User {
  id: number
  username: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  clients?: Client[]
}

interface UserCreate {
  username: string
  email: string
  full_name: string
  password: string
  role: string
  client_ids?: number[]
}

interface Client {
  id: number
  name: string
  address?: string
  contact_person?: string
  phone?: string
  email?: string
}

interface UsersManagementProps {
  token: string
}

export default function UsersManagement({ token }: UsersManagementProps) {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newUser, setNewUser] = useState<UserCreate>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user'
  })
  const { showSuccess, showError, showConfirm } = useNotificationContext()
  const { canResetUserPassword } = useUser(token)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [selectedUserForClients, setSelectedUserForClients] = useState<User | null>(null)
  const [userClients, setUserClients] = useState<Client[]>([])
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [resettingPassword, setResettingPassword] = useState(false)

  useEffect(() => {
    fetchUsers()
    fetchCurrentUser()
    fetchClients()
  }, [])

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const userData = await response.json()
        setCurrentUserId(userData.id)
        setCurrentUser(userData)
        console.log('Pobrano dane aktualnego użytkownika:', userData)
      } else {
        console.error('Błąd podczas pobierania danych aktualnego użytkownika:', response.status)
      }
    } catch (error) {
      console.error('Błąd podczas pobierania danych aktualnego użytkownika:', error)
    }
  }

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/clients/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setClients(data)
      } else {
        console.error('Błąd podczas ładowania referatów')
      }
    } catch (error) {
      console.error('Błąd połączenia z serwerem podczas ładowania referatów')
    }
  }

  const fetchUserClients = async (userId: number) => {
    setLoadingClients(true)
    try {
      const response = await fetch(`/api/users/${userId}/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUserClients(data)
        setSelectedClientIds(data.map((client: Client) => client.id))
      } else {
        showError('Błąd podczas ładowania przypisań referatów')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setLoadingClients(false)
    }
  }

  const handleManageUserClients = (user: User) => {
    setSelectedUserForClients(user)
    fetchUserClients(user.id)
  }

  const handleSaveUserClients = async () => {
    if (!selectedUserForClients) return

    try {
      const response = await fetch(`/api/users/${selectedUserForClients.id}/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedUserForClients.id,
          client_ids: selectedClientIds,
        }),
      })

      if (response.ok) {
        showSuccess('Przypisania referatów zostały zaktualizowane')
        setSelectedUserForClients(null)
        setUserClients([])
        setSelectedClientIds([])
        fetchUsers() // Odśwież listę użytkowników
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas zapisywania przypisań')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleClientToggle = (clientId: number) => {
    setSelectedClientIds(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    )
  }

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data)
      } else {
        showError('Błąd podczas ładowania użytkowników')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.full_name || !newUser.password) {
      showError('Wszystkie pola są wymagane')
      return
    }

    try {
      const response = await fetch('/api/users/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      })

      if (response.ok) {
        showSuccess('Użytkownik został utworzony')
        setNewUser({
          username: '',
          email: '',
          full_name: '',
          password: '',
          role: 'user'
        })
        setShowCreateForm(false)
        fetchUsers()
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas tworzenia użytkownika')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleUpdateUserRole = async (userId: number, newRole: string) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      })

      if (response.ok) {
        showSuccess('Rola użytkownika została zaktualizowana')
        fetchUsers()
        setSelectedUser(null)
        setSelectedRole('')
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas aktualizacji roli')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !isActive }),
      })

      if (response.ok) {
        showSuccess(`Użytkownik został ${!isActive ? 'aktywowany' : 'dezaktywowany'}`)
        fetchUsers()
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas zmiany statusu użytkownika')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  const handleResetPassword = async () => {
    if (!selectedUserForPasswordReset || !newPassword.trim()) {
      showError('Wprowadź nowe hasło')
      return
    }

    if (newPassword !== confirmNewPassword) {
      showError('Hasła nie są identyczne')
      return
    }

    if (newPassword.length < 6) {
      showError('Hasło musi mieć co najmniej 6 znaków')
      return
    }

    setResettingPassword(true)
    try {
      const response = await fetch(`/api/users/${selectedUserForPasswordReset.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_password: newPassword,
        }),
      })

      if (response.ok) {
        showSuccess(`Hasło użytkownika ${selectedUserForPasswordReset.username} zostało zresetowane`)
        setSelectedUserForPasswordReset(null)
        setNewPassword('')
        setConfirmNewPassword('')
      } else {
        const error = await response.json()
        showError(error.detail || 'Błąd podczas resetowania hasła')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    } finally {
      setResettingPassword(false)
    }
  }

  const handleDeleteUser = async (user: User) => {
    console.log('Próba usunięcia użytkownika:', user.username, 'ID:', user.id)
    console.log('CurrentUserId:', currentUserId)
    console.log('Current user role:', currentUser?.role)

    const confirmed = await showConfirm(
      'Potwierdź usunięcie',
      `Czy na pewno chcesz usunąć użytkownika "${user.username}"? Ta operacja jest nieodwracalna.`
    )

    console.log('Potwierdzenie usunięcia:', confirmed)
    if (!confirmed) return

    setDeletingId(user.id)
    try {
      console.log('Wysyłanie żądania DELETE na:', `/api/users/${user.id}`)
      const res = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      console.log('Odpowiedź z API:', res.status, res.statusText)

      if (res.ok) {
        // optymistyczna aktualizacja listy (bez dodatkowego fetch)
        setUsers(prev => prev.filter(u => u.id !== user.id))
        showSuccess('Użytkownik został usunięty')
        return
      }

      // odczytaj detail, jeśli jest
      let detail = ''
      let errorData = null
      try {
        errorData = await res.json()
        detail = errorData.detail || ''
        console.log('Pełne szczegóły błędu:', errorData)
      } catch (parseError) {
        console.log('Nie można sparsować odpowiedzi błędu:', parseError)
      }

      switch (res.status) {
        case 400:
          showError(detail || 'Nie możesz usunąć własnego konta')
          break
        case 401:
          showError('Sesja wygasła. Zaloguj się ponownie.')
          break
        case 403:
          showError(`Brak uprawnień: ${detail || 'wymagany MASTER_ADMIN'}`)
          break
        case 404:
          showError('Użytkownik nie istnieje.')
          break
        default:
          showError(detail || `Błąd podczas usuwania użytkownika (kod: ${res.status})`)
      }
    } catch (error) {
      console.error('Błąd podczas usuwania użytkownika:', error)
      showError('Błąd połączenia z serwerem')
    } finally {
      setDeletingId(null)
    }
  }


  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'MASTER_ADMIN':
        return 'bg-purple-100 text-purple-800'
      case 'ADMIN':
        return 'bg-red-100 text-red-800'
      case 'KIEROWNIK':
        return 'bg-orange-100 text-orange-800'
      case 'URZEDNIK':
        return 'bg-yellow-100 text-yellow-800'
      case 'USER':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'MASTER_ADMIN':
        return 'Master Admin'
      case 'ADMIN':
        return 'Administrator'
      case 'KIEROWNIK':
        return 'Kierownik'
      case 'URZEDNIK':
        return 'Urzędnik'
      case 'USER':
        return 'Użytkownik'
      default:
        return role
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Zarządzanie użytkownikami</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          + Dodaj użytkownika
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Nowy użytkownik</h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nazwa użytkownika
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="np. jan.kowalski"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="jan.kowalski@firma.pl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Imię i nazwisko
              </label>
              <input
                type="text"
                value={newUser.full_name}
                onChange={(e) => setNewUser(prev => ({ ...prev, full_name: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Jan Kowalski"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Hasło
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rola
              </label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="USER">Użytkownik</option>
                <option value="URZEDNIK">Urzędnik</option>
                <option value="KIEROWNIK">Kierownik</option>
                <option value="ADMIN">Administrator</option>
                <option value="MASTER_ADMIN">Master Admin</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowCreateForm(false)
                setNewUser({
                  username: '',
                  email: '',
                  full_name: '',
                  password: '',
                  role: 'USER'
                })
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Anuluj
            </button>
            <button
              onClick={handleCreateUser}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Utwórz użytkownika
            </button>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Użytkownik
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rola
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {currentUser?.role === 'MASTER_ADMIN' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referaty
                    </th>
                  )}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          @{user.username}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Aktywny' : 'Nieaktywny'}
                      </span>
                    </td>
                    {currentUser?.role === 'MASTER_ADMIN' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => handleManageUserClients(user)}
                          className="text-purple-600 hover:text-purple-900 text-xs"
                        >
                          Zarządzaj referatami
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user)
                          setSelectedRole(user.role)
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edytuj rolę
                      </button>
                      {canResetUserPassword() && user.id !== currentUserId && (
                        <button
                          onClick={() => {
                            setSelectedUserForPasswordReset(user)
                            setNewPassword('')
                            setConfirmNewPassword('')
                          }}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          Reset hasła
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                        className={`${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                      >
                        {user.is_active ? 'Dezaktywuj' : 'Aktywuj'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        disabled={deletingId === user.id || user.id === currentUserId}
                        title={user.id === currentUserId ? 'Nie możesz usunąć własnego konta' : ''}
                        className={`${
                        user.id === currentUserId ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'
                         } inline-flex items-center gap-1`}
                        >
                         {deletingId === user.id ? (
                         <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                           ) : null}
                              Usuń
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Role Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Zmień rolę użytkownika
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Użytkownik: <strong>{selectedUser.full_name}</strong> (@{selectedUser.username})
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aktualna rola: <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(selectedUser.role)}`}>
                    {getRoleDisplayName(selectedUser.role)}
                  </span>
                </label>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nowa rola:
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="USER">Użytkownik</option>
                  <option value="URZEDNIK">Urzędnik</option>
                  <option value="KIEROWNIK">Kierownik</option>
                  <option value="ADMIN">Administrator</option>
                  <option value="MASTER_ADMIN">Master Admin</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedUser(null)
                    setSelectedRole('')
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Anuluj
                </button>
                <button
                  onClick={() => handleUpdateUserRole(selectedUser.id, selectedRole)}
                  disabled={!selectedRole || selectedRole === selectedUser.role}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zatwierdź zmianę
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage User Clients Modal */}
      {selectedUserForClients && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Zarządzanie przypisaniami referatów
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Użytkownik: <strong>{selectedUserForClients.full_name}</strong> (@{selectedUserForClients.username})
              </p>
              
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Ładowanie referatów...</span>
                </div>
              ) : (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Wybierz referaty, do których użytkownik ma mieć dostęp:
                  </label>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                    {clients.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        Brak dostępnych referatów
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {clients.map((client) => (
                          <div key={client.id} className="p-3 hover:bg-gray-50">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedClientIds.includes(client.id)}
                                onChange={() => handleClientToggle(client.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="ml-3 flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {client.name}
                                </div>
                                {client.address && (
                                  <div className="text-xs text-gray-500">
                                    {client.address}
                                  </div>
                                )}
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-md">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Informacja o przypisaniach
                        </h3>
                        <div className="mt-1 text-sm text-blue-700">
                          <p>Użytkownik będzie miał dostęp tylko do danych z wybranych referatów.</p>
                          <p className="mt-1">Jeśli nie wybierzesz żadnego referatu, użytkownik nie będzie miał dostępu do żadnych danych.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedUserForClients(null)
                    setUserClients([])
                    setSelectedClientIds([])
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSaveUserClients}
                  disabled={loadingClients}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zapisz przypisania
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {selectedUserForPasswordReset && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reset hasła użytkownika
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Użytkownik: <strong>{selectedUserForPasswordReset.full_name}</strong> (@{selectedUserForPasswordReset.username})
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nowe hasło
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Wprowadź nowe hasło"
                  minLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Hasło musi mieć co najmniej 6 znaków
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Potwierdź nowe hasło
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Potwierdź nowe hasło"
                  minLength={6}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedUserForPasswordReset(null)
                    setNewPassword('')
                    setConfirmNewPassword('')
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !newPassword.trim() || !confirmNewPassword.trim() || newPassword !== confirmNewPassword || newPassword.length < 6}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingPassword ? (
                    <>
                      <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Resetowanie...
                    </>
                  ) : (
                    'Resetuj hasło'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
